import { getAnnouncements } from '@/services/announcements';
import { IMyInfo } from '@/services/user/types';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { useTranslatedRoutes } from '@/routes';
import { checkIsParent } from '@/utils/helpers/common';
import { useTranslation } from 'react-i18next';
import { getHomeworks } from '@/services/homeworks';
import { getMessageGroups } from '@/services/messaging';
import { UserMenu } from '@/components/UserMenu';
import {
  HomeworkIcon,
  MessageIcon,
  BellIcon,
} from '@/components/CustomIcon/icons';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { ChevronRight } from 'lucide-react';

interface CustomNavbarProps {
  myInfo: IMyInfo;
}

export const CustomNavbar = ({ myInfo }: CustomNavbarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const routes = useTranslatedRoutes({ isParent: checkIsParent(), myInfo });

  const { data: announcementsData } = useQuery({
    queryKey: ['announcements', { status: false }],
    queryFn: () => getAnnouncements({ status: false }),
  });

  const { data: homeworksData } = useQuery({
    queryKey: ['homeworks', { isCompleted: false }],
    queryFn: () => getHomeworks({ page: 1, perPage: 10, isCompleted: false }),
  });

  const { data: messagesData } = useQuery({
    queryKey: ['messages', { unread: true }],
    queryFn: () => getMessageGroups({ page: 1, perPage: 10 }),
  });

  const activeRoute = routes.find((route) => {
    if (route.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(route.path);
  });

  const isDetailPage =
    activeRoute &&
    location.pathname !== activeRoute.path &&
    location.pathname.startsWith(activeRoute.path);
  const breadcrumbItems = isDetailPage
    ? [
        { name: activeRoute.name, path: activeRoute.path },
        {
          name: t('home.homeworks.detail', { defaultValue: 'Detay' }),
          path: null,
        },
      ]
    : activeRoute
      ? [{ name: activeRoute.name, path: null }]
      : [];

  const unreadMessagesCount =
    messagesData?.items.reduce(
      (acc, group) => acc + (group.newMessages || 0),
      0
    ) || 0;

  const navItems = [
    {
      label: t('home.homeworks.title'),
      icon: HomeworkIcon,
      count: homeworksData?.totalCount || 0,
      path: '/homeworks',
    },
    {
      label: t('routes.messaging.name'),
      icon: MessageIcon,
      count: unreadMessagesCount,
      path: '/messaging',
    },
    {
      label: t('routes.announcements.name'),
      icon: BellIcon,
      count: announcementsData?.totalCount || 0,
      path: '/announcements',
    },
  ];

  return (
    <nav className="w-full p-6 flex items-center justify-between border-b">
      <div className="flex items-center gap-3 lg:gap-0">
        <SidebarTrigger className="md:hidden shrink-0" />
        <div className="flex items-center gap-1.5 text-sm text-nowrap">
          {breadcrumbItems.map((item, index) => (
            <div key={index} className="flex items-center gap-1.5">
              {item.path ? (
                <Link
                  to={item.path}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.name}
                </Link>
              ) : (
                <span className="text-foreground">{item.name}</span>
              )}
              {index < breadcrumbItems.length - 1 && (
                <ChevronRight className="size-3.5 text-muted-foreground" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="flex items-center gap-2.5 px-2.5 group transition-colors"
          >
            <div className="relative">
              <item.icon className="text-muted-foreground size-5" />

              {item.count > 0 && (
                <span className="text-[10px] lg:text-xs size-3 lg:size-4 flex items-center justify-center bg-red-500 text-white rounded-full absolute -top-1 lg:-top-1.5 -right-1 lg:-right-1.5 font-medium">
                  {item.count > 9 ? '9+' : item.count}
                </span>
              )}
            </div>
            <span className="text-muted-foreground hidden lg:inline text-sm">
              {item.label}
            </span>
          </Link>
        ))}
        <UserMenu myInfo={myInfo} />
      </div>
    </nav>
  );
};
